import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { InfoService } from '../../../shared/services/info.service';
import { LfwRouterService } from '../../../route/lfw-router.service';
import { combineLatest, firstValueFrom, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ProjectsService } from '../../../projects/projects.service';
import { OrdersService } from '../../orders.service';
import { LFOrderList } from '@lfw/roesberg.liveforms.core';
import { SelectionArguments } from '../../../shared/models/selection-arguments';
import { OnlineOrderListComponent } from '../online-order-list/online-order-list.component';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../../../shared/dialogs/error-dialog/error-dialog.component';

@Component({
  selector: 'lfw-online-order-list-view',
  templateUrl: './online-order-list-view.component.html',
  styleUrl: './online-order-list-view.component.css',
  imports: [OnlineOrderListComponent]
})
export class OnlineOrderListViewComponent implements OnInit {
  projectId = '';
  lastSelectedId = '';
  private projectSubscription!: Subscription;
  orders: LFOrderList | undefined = undefined;

  private readonly routerService = inject(LfwRouterService);
  private readonly projectService = inject(ProjectsService);
  private readonly ordersService = inject(OrdersService);
  private readonly route = inject(ActivatedRoute);
  private readonly infoService = inject(InfoService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);

  constructor() {
    this.infoService.clearStatus();
    this.projectSubscription = this.projectService.projectChange.subscribe(() => {
      this.clearOrders();
    });
  }

  ngOnInit(): void {
    let selectedId: string; // last selected item in regular selection mode
    this.projectId = '';
    this.projectSubscription.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap])
        .pipe(
          switchMap(([paramMap, queryParamMap]) => {
            selectedId = queryParamMap.get('selectedId') ?? '';
            this.projectId = paramMap.get('project') ?? '';
            return this.projectService.selectProject(this.projectId);
          }),
          switchMap((selectedProjectOk) => this.loadOrders(selectedProjectOk, selectedId))
        )
        .subscribe()
    );
  }

  refreshOrders(): void {
    if (this.projectId) {
      this.loadOrders(true, this.lastSelectedId).subscribe();
    }
  }

  private loadOrders(selectedProjectOk: boolean, selectedId: string): Observable<LFOrderList> {
    if (!selectedProjectOk) {
      this.orders = new LFOrderList();
      this.lastSelectedId = '';
      this.infoService.setStatusTranslate('PROJECTS.CANTACTIVATE');
      return of(this.orders);
    }

    return this.ordersService.getOnlineOrders().pipe(
      tap((res) => {
        this.orders = res;
        this.lastSelectedId = selectedId ?? '';
        this.infoService.setLoadedItems(res.data.length);
      }),
      catchError((err) => {
        firstValueFrom(
          this.dialog
            .open(ErrorDialogComponent, {
              data: {
                title: this.translate.instant('DIALOGS.ERROR'),
                message: err.error.detail || 'ORDERS.LOADING_ERROR'
              }
            })
            .afterClosed()
            .pipe(tap(() => this.routerService.navigateToOrdersRoot()))
        );
        return throwError(() => err);
      })
    );
  }

  itemSelected(args: SelectionArguments): void {
    if (!args?.id) {
      return;
    }
    this.routerService.navigateToForm(args.id);
  }

  private clearOrders(): void {
    this.orders = undefined;
  }
}
